// ============================================================================
// components/block/liveStreamManagment/DeckMixerEffectsModal.tsx
//
// Fenêtre des effets audio d'une source, ouverte par l'engrenage d'une voie du
// bloc Mixeur du deck (2026-10-21). À gauche, les tuiles des effets les unes sous
// les autres (mêmes tuiles que la config de la source : clic = on/off) ; à
// droite, les réglages de l'effet dont on a cliqué l'engrenage.
//
// En direct, comme le reste du bloc Mixeur : chaque clic et chaque réglage passe
// par onToggleAudioEffect / onAudioEffectConfigsChange, les mêmes que la config
// de la source — les deux restent synchronisées. Pas d'Annuler ni de Valider.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Activity, Settings, X } from 'lucide-react';
import { AUDIO_EFFECTS } from './audioEffects/registry';
import { EFFECT_BUTTON_HEIGHT } from './EffectCarousel';
import { AudioEffectButton, AudioEffectState, DEFAULT_AUDIO_EFFECTS } from './SourceConfigModal';

interface DeckMixerEffectsModalProps {
  sourceId      : string;
  label         : string;
  effects      ?: AudioEffectState;
  effectConfigs?: Record<string, unknown>;
  onToggle      : (sourceId: string, effectId: string) => void;
  onConfigsChange?: (sourceId: string, configs: Record<string, unknown>) => void;
  onClose       : () => void;
}

const TILE_COL_W = 140;
const TILE_GAP   = 8;
// Marge autour des tuiles : le contour de la tuile choisie (2px + 2px d'écart)
// déborde de la tuile — sans elle, il serait rogné en haut de la première et en
// bas de la dernière.
const TILE_PAD   = 4;
// Exactement 5 tuiles entières visibles, les suivantes par l'ascenseur.
const VISIBLE_TILES = 5;
const TILE_COL_H = VISIBLE_TILES * EFFECT_BUTTON_HEIGHT + (VISIBLE_TILES - 1) * TILE_GAP + 2 * TILE_PAD;

const DeckMixerEffectsModal: React.FC<DeckMixerEffectsModalProps> = ({
  sourceId, label, effects = DEFAULT_AUDIO_EFFECTS, effectConfigs, onToggle, onConfigsChange, onClose,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Réglages en cours : plusieurs mouvements d'un curseur peuvent arriver avant que
  // le parent ne renvoie la nouvelle config. Resynchronisés sur toute config venue
  // d'ailleurs (config de la source ouverte en parallèle).
  const configsRef = useRef<Record<string, unknown>>(effectConfigs ?? {});
  useEffect(() => { configsRef.current = effectConfigs ?? {}; }, [effectConfigs]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const fx = AUDIO_EFFECTS.find(f => f.id === selectedId) ?? null;
  const FxIcon = fx?.icon;
  const config = fx ? ((effectConfigs ?? {})[fx.id] ?? fx.defaultConfig) : null;

  const handleConfigChange = (effectId: string, cfg: unknown) => {
    const next = { ...configsRef.current, [effectId]: cfg };
    configsRef.current = next;
    onConfigsChange?.(sourceId, next);
  };

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)', border: '1px solid #334155', borderRadius: 14,
        width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 48px rgba(0,0,0,0.55)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid #334155', flexShrink: 0 }}>
          <Activity style={{ width: 16, height: 16, color: '#e879f9' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Effets audio — {label}
          </span>
          <button onClick={onClose} title="Fermer" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 4, borderRadius: 6 }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 16, padding: 16, height: TILE_COL_H + 32, boxSizing: 'border-box', flexShrink: 0 }}>
          {/* ── Tuiles des effets, les unes sous les autres ── */}
          <div style={{
            width: TILE_COL_W, height: TILE_COL_H, flexShrink: 0, boxSizing: 'border-box',
            display: 'flex', flexDirection: 'column', gap: TILE_GAP,
            overflowY: 'auto', padding: TILE_PAD,
            // Ascenseur à gauche : la colonne est en sens droite-à-gauche, chaque
            // tuile est remise en gauche-à-droite pour que son contenu ne s'inverse pas.
            direction: 'rtl',
          }}>
            {AUDIO_EFFECTS.map(effect => (
              <div key={effect.id} style={{
                direction: 'ltr',
                borderRadius: 12, flexShrink: 0,
                outline: selectedId === effect.id ? '2px solid #e879f9' : 'none', outlineOffset: 2,
              }}>
                <AudioEffectButton
                  fx={effect}
                  active={!!effects[effect.id]}
                  onToggle={() => onToggle(sourceId, effect.id)}
                  onOpenSettings={() => setSelectedId(effect.id)}
                />
              </div>
            ))}
          </div>

          {/* ── Réglages de l'effet choisi ── */}
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', borderLeft: '1px solid rgba(255,255,255,0.08)', paddingLeft: 16 }}>
            {!fx ? (
              <div style={{ height: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'rgba(148,163,184,0.7)', fontSize: 12, textAlign: 'center' }}>
                <Settings style={{ width: 18, height: 18, opacity: 0.6 }} />
                Cliquez l'engrenage d'un effet pour le régler.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  {FxIcon && <FxIcon style={{ width: 16, height: 16, color: '#e879f9' }} />}
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{fx.label}</span>
                </div>
                <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5, marginBottom: fx.disabled ? 0 : 12 }}>{fx.description}</div>
                {fx.disabled ? (
                  <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)' }}>{fx.disabledReason}</div>
                ) : (
                  fx.renderControls({ config, onChange: cfg => handleConfigChange(fx.id, cfg) })
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeckMixerEffectsModal;
